import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../AuthProvider';
import bgImg from "../../../assets/bg-Img.jpg";
import mobileBgImg from "../../../assets/mobile-bg.png";
import heroVideo from "../../../assets/hero.mp4";
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Volume2, VolumeX } from 'lucide-react';
import { Nav } from '../../Nav';


export const HeroSection = () => {

    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [showVideoPopup, setShowVideoPopup] = useState(false);
    const [isMuted, setIsMuted] = useState(true);

    // Show video popup on component mount (once per day)
    useEffect(() => {
        const today = new Date().toDateString();
        const lastShown = localStorage.getItem('heroVideoLastShown');
        
        // Only show if not shown today
        if (lastShown !== today) {
            const timer = setTimeout(() => {
                setShowVideoPopup(true);
                // Update localStorage when video is shown
                localStorage.setItem('heroVideoLastShown', today);
            }, 1000); // Show after 1 second

            return () => clearTimeout(timer);
        }
    }, []);

    const handleGetStarted = async () => {
        if (currentUser) {
          // If already logged in, go directly to dashboard
          navigate("/recentactivities");
        } else {
          // Otherwise, redirect to login/signup
          navigate("/login");
        }
      };

    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
      };

    // Video popup component
    const VideoPopup = () => (
        <AnimatePresence>
            {showVideoPopup && (
                <motion.div 
                    className="fixed inset-0 flex justify-center items-center backdrop-blur-lg z-50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div 
                        className="relative bg-black rounded-lg overflow-hidden max-w-4xl w-full mx-4"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ type: "spring", damping: 15 }}
                    >
                        <button 
                            onClick={() => setShowVideoPopup(false)}
                            className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 transition-colors duration-200 z-10"
                            aria-label="Close video"
                        >
                            <X className="w-6 h-6 text-white" />
                        </button>
                        <button 
                            onClick={() => setIsMuted(!isMuted)}
                            className="absolute top-4 left-4 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 transition-colors duration-200 z-10"
                            aria-label={isMuted ? "Unmute video" : "Mute video"}
                        >
                            {isMuted ? (
                                <VolumeX className="w-6 h-6 text-white" />
                            ) : (
                                <Volume2 className="w-6 h-6 text-white" />
                            )}
                        </button>
                        <video 
                            className="w-full h-auto max-h-[80vh] object-contain"
                            autoPlay
                            muted={isMuted}
                            playsInline
                            onEnded={() => setShowVideoPopup(false)}
                            onError={() => setShowVideoPopup(false)}
                        >
                            <source src={heroVideo} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

  return (
    <>
    <VideoPopup />
    <div className="relative w-full h-screen overflow-hidden  bg-[#E5E4E2] mb-0" >
      
       {/* Hero images */}
       {/* desktop */}
              <div className="hidden md:block absolute w-full h-screen bg-[#E5E4E2] md:-top-20">
               <img src={bgImg} alt="background Image" />
               
              </div>
              {/* mobile view */}
              <div className="md:hidden block  absolute w-full bg-[#E5E4E2] left-10 bottom-0">
               <img src={mobileBgImg} alt="mobile background Image" />
               
              </div>
              {/* nav */}
      <Nav />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative z-10 md:flex md:items-center md:justify-between py-0 md:py-20">
              
              {/* Hero content */}
              <div className="md:w-1/2 md:pr-10">
              {/* Social proof */}
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={{
                    ...fadeIn,
                    transition: { delay: 0.6, duration: 0.6 },
                  }}
                  className="mt-2 flex items-center mb-1 bg-white md:w-[24vw] rounded-full px-4 py-2"
                >
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4, 5].map((index) => (
                      <img
                        key={index}
                        className="inline-block h-8 w-8 rounded-full ring-2 ring-white"
                        src={`https://randomuser.me/api/portraits/${
                          index % 2 === 0 ? "women" : "men"
                        }/${20 + index}.jpg`}
                        alt=""
                      />
                    ))}
                  </div>
                  <div className="ml-3 text-[16px] font-medium text-gray-600">
                    <span className="text-indigo-600 font-bold">8,000+</span>{" "}
                    designers trust us
                  </div>
                </motion.div>
                <motion.h1
                  initial="hidden"
                  animate="visible"
                  variants={fadeIn}
                  className="text-[40px] md:text-[70px] font-bold leading-[48px] md:leading-[4.8vw] md:w-[40vw]  text-gray-900 font-[Playfair-Display]"
                > Your Entire<br/>
                 <span className="pl-3 pb-3 text-transparent italic bg-clip-text bg-gradient-to-r from-violet-600 to-indigo-500">Photoshoot.</span><br/> 
                 In a Single Click                              
                </motion.h1>
                <motion.p
                  initial="hidden"
                  animate="visible"
                  variants={{
                    ...fadeIn,
                    transition: { delay: 0.2, duration: 0.6 },
                  }}
                  className="mt-6 text-[16px] md:text-xl max-w-2xl"
                >
                 Transform your garment photos into stunning, on-model imagery instantly. Save 80% on costs, launch collections in minutes, and showcase true diversity.
                </motion.p>
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={{
                    ...fadeIn,
                    transition: { delay: 0.4, duration: 0.6 },
                  }}
                  className="mt-8 flex flex-col sm:flex-row gap-4 mb-4"
                >
                  <a
                    onClick={handleGetStarted}
                    className="cursor-pointer inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg shadow-lg bg-black text-white hover:shadow-xl transition-all duration-300"
                  >
                    Generate Your First Model (Free)
                    {/* <ArrowRight className="ml-2 h-5 w-5" /> */}
                  </a>
                </motion.div>                
              </div>             
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-0 right-0 -mt-20 -mr-20 hidden lg:block">
            <svg width="404" height="404" fill="none" viewBox="0 0 404 404">
              <defs>
                <pattern
                  id="pattern-circles"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <circle
                    cx="10"
                    cy="10"
                    r="2"
                    fill="rgba(124, 58, 237, 0.1)"
                  />
                </pattern>
              </defs>
              <rect width="404" height="404" fill="url(#pattern-circles)" />
            </svg>
          </div>
          <div className="absolute bottom-0 left-0 -mb-20 -ml-20 hidden lg:block">
            <svg width="404" height="404" fill="none" viewBox="0 0 404 404">
              <defs>
                <pattern
                  id="pattern-circles-2"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <circle
                    cx="10"
                    cy="10"
                    r="2"
                    fill="rgba(124, 58, 237, 0.1)"
                  />
                </pattern>
              </defs>
              <rect width="404" height="404" fill="url(#pattern-circles-2)" />
            </svg>
          </div>
        </div>
    </>
  )
}
